import React, { useState } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { useInView, usePrefersReducedMotion } from './features/animations/AnimationUtils';

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQSectionProps {
  title: string;
  subtitle?: string;
  questions: FAQItem[];
}

const FAQSection: React.FC<FAQSectionProps> = ({ title, subtitle, questions }) => {
  const [expanded, setExpanded] = useState<string | false>(false);
  const [headerRef, isHeaderInView] = useInView({ threshold: 0.1 });
  const prefersReducedMotion = usePrefersReducedMotion();

  const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 8, md: 12 },
        px: { xs: 2, sm: 3, md: 4 },
      }}
    >
      <Box sx={{ maxWidth: 900, mx: 'auto' }}>
        {/* Header */}
        <Box ref={headerRef} sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2rem', md: '3rem' },
              fontWeight: 800,
              color: 'white',
              mb: 2,
              opacity: isHeaderInView || prefersReducedMotion ? 1 : 0,
              transform: isHeaderInView || prefersReducedMotion ? 'translateY(0)' : 'translateY(20px)',
              transition: prefersReducedMotion ? 'none' : 'all 600ms ease-out',
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              sx={{
                fontSize: { xs: '1rem', md: '1.1rem' },
                color: 'rgba(255, 255, 255, 0.8)',
                maxWidth: 600,
                mx: 'auto',
                opacity: isHeaderInView || prefersReducedMotion ? 1 : 0,
                transform: isHeaderInView || prefersReducedMotion ? 'translateY(0)' : 'translateY(20px)',
                transition: prefersReducedMotion ? 'none' : 'all 600ms ease-out 200ms',
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>

        {/* FAQ Items */}
        <Box>
          {questions.map((item, index) => (
            <Accordion
              key={index}
              expanded={expanded === `panel${index}`}
              onChange={handleChange(`panel${index}`)}
              sx={{
                mb: 2,
                backgroundColor: 'rgba(15, 51, 89, 0.4)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '12px !important',
                overflow: 'hidden',
                '&:before': {
                  display: 'none',
                },
                '&.Mui-expanded': {
                  backgroundColor: 'rgba(15, 51, 89, 0.5)',
                  borderColor: 'rgba(255, 255, 255, 0.15)',
                },
                transition: 'all 0.3s ease',
                opacity: isHeaderInView || prefersReducedMotion ? 1 : 0,
                transform: isHeaderInView || prefersReducedMotion ? 'translateY(0)' : 'translateY(20px)',
                transitionDelay: prefersReducedMotion ? '0ms' : `${300 + index * 100}ms`,
              }}
            >
              <AccordionSummary
                expandIcon={
                  <ExpandMoreIcon 
                    sx={{ 
                      color: 'rgba(255, 255, 255, 0.9)',
                      transition: 'transform 0.3s ease',
                    }} 
                  />
                }
                aria-controls={`panel${index}bh-content`}
                id={`panel${index}bh-header`}
                sx={{
                  minHeight: 64,
                  '& .MuiAccordionSummary-content': {
                    my: 2,
                  },
                  '&.Mui-expanded': {
                    minHeight: 64,
                  },
                }}
              >
                <Typography
                  sx={{
                    color: 'white',
                    fontWeight: 600,
                    fontSize: { xs: '1rem', md: '1.1rem' },
                  }}
                >
                  {item.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  pt: 0,
                  pb: 3,
                  px: 3,
                }}
              >
                <Typography
                  sx={{
                    color: 'rgba(255, 255, 255, 0.85)',
                    fontSize: { xs: '0.95rem', md: '1rem' },
                    lineHeight: 1.7,
                  }}
                >
                  {item.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default FAQSection;